import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroBackground from "@/assets/hero-background.jpg";
import claraPortrait from "@/assets/clara-portrait-final.png";
import PillarCTAWithPhoto from "@/components/PillarCTAWithPhoto";

export const faqItems = [
  {
    question: "Quanto custa comer bem como estudante na Austrália?",
    answer: "Com planejamento, é possível manter uma alimentação equilibrada com AUD 50 a AUD 80 por semana. O segredo é comprar na estação, usar marcas próprias e fazer meal prep."
  },
  {
    question: "Qual o mercado mais barato na Austrália?",
    answer: "Geralmente o Aldi é o mais barato para itens básicos. Mas acompanhar as promoções semanais do Coles e Woolworths pelo app pode trazer economia ainda maior em itens específicos."
  },
  {
    question: "Como conciliar estudo, trabalho e alimentação saudável?",
    answer: "Planejamento é a chave. Separar um momento na semana para fazer meal prep (cozinhar em quantidade) resolve boa parte do problema. No acompanhamento, montamos essa rotina juntos."
  },
  {
    question: "Posso emagrecer gastando pouco?",
    answer: "Sim. Alimentos saudáveis não precisam ser caros. Arroz, feijão, ovos, frutas da estação e vegetais são acessíveis e fazem a base de uma alimentação equilibrada."
  },
  {
    question: "O que levar de lanche para a universidade?",
    answer: "Frutas, castanhas em saquinhos, sanduíches naturais, iogurte e barrinhas caseiras são opções práticas e baratas que cabem na mochila."
  },
  {
    question: "Como funciona a primeira consulta?",
    answer: "É uma conversa de cerca de 30 minutos, sem custo, por videochamada ou no consultório em Hamilton. Eu entendo seus objetivos e sua rotina de estudo e trabalho e explico como funciona o acompanhamento. Depois você decide, com calma, se quer seguir."
  },
  {
    question: "O acompanhamento funciona para quem tem orçamento apertado?",
    answer: "Sim. O plano é montado respeitando seu orçamento. Eu trabalho com os produtos mais acessíveis e estratégias de economia no mercado."
  },
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button size="lg" variant="gradient" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto" style={{ background: 'var(--gradient-green)' }} onClick={openDialog}>
      <Calendar className="mr-2 h-5 w-5" />Agendar consulta
    </Button>
    <Button size="lg" variant="gradient-outline" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto" onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }} onClick={() => window.open('https://wa.me/61423038977', '_blank')}>
      <MessageCircle className="mr-2 h-5 w-5" />Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />
        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">Clara Camille · Nutricionista</p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">Dieta para estudantes brasileiros na Austrália: como comer bem gastando pouco</h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">Orçamento apertado não significa comer mal. Aqui você encontra dicas práticas, lista de compras econômica e receitas rápidas para se alimentar bem como estudante na Austrália.</p>
              <CTAButtons openDialog={openDialog} />
            </div>
          </section>

          {/* CONTEÚDO */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="prose prose-lg max-w-none space-y-12">

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">O desafio de comer bem com orçamento de estudante</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Ser estudante na Austrália significa lidar com mensalidade, aluguel, transporte e ainda tentar comer bem. É muita coisa. A alimentação costuma ser a primeira a ser cortada, e muitos estudantes acabam vivendo de miojo, pão e fast food.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">A boa notícia é que comer bem na Austrália não precisa ser caro. Com planejamento e as escolhas certas, é possível montar refeições nutritivas e gostosas gastando menos do que você imagina.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Orçamento semanal de alimentação para estudantes</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Veja um exemplo de como distribuir seus gastos semanais com alimentação:</p>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm sm:text-base">
                      <thead>
                        <tr className="bg-primary/10">
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Categoria</th>
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Valor estimado (AUD/semana)</th>
                          <th className="text-left p-3 sm:p-4 font-semibold text-foreground font-sans">Itens principais</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr><td className="p-3 sm:p-4 text-foreground font-sans font-medium">Proteínas</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">AUD 15-20</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">Ovos, frango (coxa/sobrecoxa), carne moída, atum em lata</td></tr>
                        <tr><td className="p-3 sm:p-4 text-foreground font-sans font-medium">Carboidratos</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">AUD 8-12</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">Arroz, pão, aveia, batata, macarrão</td></tr>
                        <tr><td className="p-3 sm:p-4 text-foreground font-sans font-medium">Frutas e vegetais</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">AUD 12-18</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">Banana, maçã, brócolis, cenoura, tomate (da estação)</td></tr>
                        <tr><td className="p-3 sm:p-4 text-foreground font-sans font-medium">Laticínios e outros</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">AUD 8-12</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">Leite, iogurte, queijo, manteiga</td></tr>
                        <tr><td className="p-3 sm:p-4 text-foreground font-sans font-medium">Snacks e extras</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">AUD 5-8</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">Castanhas, café, temperos</td></tr>
                        <tr className="bg-primary/5"><td className="p-3 sm:p-4 text-foreground font-sans font-bold">Total estimado</td><td className="p-3 sm:p-4 text-foreground font-sans font-bold">AUD 48-70</td><td className="p-3 sm:p-4 text-muted-foreground font-sans">Por pessoa, por semana</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Lista de compras semanal econômica</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Uma lista base que funciona para a maioria dos estudantes:</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h3 className="text-lg font-typewriter font-bold text-foreground">Proteínas</h3>
                      <ul className="space-y-1">
                        {["1 dúzia de ovos", "1 kg frango (coxa/sobrecoxa)", "500g carne moída", "2 latas de atum"].map((item, i) => (
                          <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary flex-shrink-0" /><span className="text-sm text-muted-foreground font-sans">{item}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-typewriter font-bold text-foreground">Carbs e grãos</h3>
                      <ul className="space-y-1">
                        {["1 kg arroz", "1 pão integral", "500g aveia", "1 kg batata", "500g macarrão"].map((item, i) => (
                          <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary flex-shrink-0" /><span className="text-sm text-muted-foreground font-sans">{item}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-typewriter font-bold text-foreground">Frutas e vegetais</h3>
                      <ul className="space-y-1">
                        {["Bananas", "Maçãs", "Brócolis ou couve-flor", "Cenoura", "Tomate", "Alface ou espinafre"].map((item, i) => (
                          <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary flex-shrink-0" /><span className="text-sm text-muted-foreground font-sans">{item}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-typewriter font-bold text-foreground">Outros</h3>
                      <ul className="space-y-1">
                        {["1 litro de leite", "1 iogurte natural", "Café", "Azeite ou óleo", "Sal, alho, cebola"].map((item, i) => (
                          <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary flex-shrink-0" /><span className="text-sm text-muted-foreground font-sans">{item}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <PillarCTAWithPhoto
                  title="Comer bem com orçamento de estudante"
                  message="Eu monto planos alimentares que respeitam o seu bolso de estudante. Na consulta inicial, olhamos juntos para o seu orçamento real e eu explico como o plano se ajusta a ele."
                  openDialog={openDialog}
                />

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Receitas rápidas e baratas</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Aqui vão ideias de refeições que custam pouco e ficam prontas em menos de 30 minutos:</p>
                  <ul className="space-y-3">
                    {[
                      "Stir-fry de frango com legumes e arroz (usa o que tiver na geladeira)",
                      "Omelete de vegetais com pão integral",
                      "Macarrão com atum, tomate e azeite",
                      "Bowl de arroz com feijão, ovo frito e salada",
                      "Sopa de legumes com frango desfiado (rende para vários dias)",
                      "Wrap de frango com alface, tomate e iogurte como molho",
                      "Overnight oats: aveia, leite e banana (prepara à noite, come de manhã)",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-base sm:text-lg text-muted-foreground font-sans">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Dicas de economia no mercado</h2>
                  <ul className="space-y-3">
                    {[
                      "Baixe os apps do Coles e Woolworths para ver as promoções da semana",
                      "Compre frutas e vegetais da estação (mais baratos e mais frescos)",
                      "Prefira cortes de carne mais baratos: coxa de frango, carne moída, atum em lata",
                      "Marcas próprias (Coles Brand, Woolworths Essentials) são mais baratas e de boa qualidade",
                      "Compre em quantidade e congele: arroz, feijão e proteínas rendem mais assim",
                      "Vá ao Aldi para itens básicos: ovos, leite, pão, frutas",
                      "Evite comprar preparados e fatiados (cortar você mesmo é muito mais barato)",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-base sm:text-lg text-muted-foreground font-sans">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Como conciliar estudo, trabalho e alimentação</h2>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">A maioria dos estudantes brasileiros na Austrália trabalha e estuda ao mesmo tempo. A rotina é puxada e sobra pouco tempo para cozinhar. A solução mais eficiente é o meal prep: escolher um dia da semana (geralmente domingo) e preparar as refeições dos próximos dias.</p>
                  <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Outra dica é levar comida pronta para a universidade em vez de comprar no campus (onde os preços são altos). Um sanduíche e uma fruta custam uma fração do que você pagaria no food court.</p>
                </div>

                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-typewriter font-bold text-foreground">Leia também no blog</h2>
                  <ul className="space-y-3">
                    <li><Link to="/blog/$slug" params={{ slug: "o-que-comprar-no-coles-e-woolworths-para-comer-melhor" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">O que comprar no Coles e Woolworths para comer melhor</Link></li>
                    <li><Link to="/blog/$slug" params={{ slug: "como-montar-marmitas-na-australia-sem-passar-horas-cozinhando" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Como montar marmitas na Austrália sem passar horas cozinhando</Link></li>
                    <li><Link to="/blog/$slug" params={{ slug: "como-bater-proteina-na-australia-com-opcoes-acessiveis" }} className="text-base sm:text-lg font-sans gradient-text italic hover:opacity-80 transition-opacity">Como bater proteína na Austrália com opções acessíveis</Link></li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">Tire suas dúvidas</p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">Perguntas frequentes</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`faq-${index}`} className="rounded-xl border border-border/50 bg-card px-5 sm:px-6">
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">{item.question}</AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA FINAL */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div className="absolute inset-0 blur-sm scale-105" style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />
            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16" style={{ background: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)', border: '1px solid rgba(255, 255, 255, 0.25)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                <img src={claraPortrait} alt="Clara Camille" className="w-20 h-20 rounded-full object-cover object-top border-2 border-white/50 shadow-lg mx-auto" loading="lazy" />
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">Quer um plano que caiba no seu orçamento de estudante?</h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">Na consulta inicial, entendo sua rotina de estudo e trabalho e explico como montamos juntos um plano alimentar prático, econômico e que funcione no seu dia a dia.</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button size="lg" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto" onClick={openDialog}><Calendar className="mr-2 h-5 w-5" />Agendar consulta</Button>
                  <Button size="lg" className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto" style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }} onClick={() => window.open('https://wa.me/61423038977', '_blank')}><MessageCircle className="mr-2 h-5 w-5" />Falar no WhatsApp</Button>
                </div>
              </div>
            </div>
          </section>

          {/* LINKS INTERNOS */}
          <section className="bg-card py-10 sm:py-14">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center space-y-4">
              <p className="text-sm text-muted-foreground font-sans">Navegue pelo site:</p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                <Link to="/" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Página inicial</Link>
                <span className="text-border">·</span>
                <Link to="/guia-alimentacao-brasileiros-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Guia de alimentação</Link>
                <span className="text-border">·</span>
                <Link to="/mercado-australiano-o-que-comprar" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Mercado australiano</Link>
                <span className="text-border">·</span>
                <Link to="/como-emagrecer-morando-na-australia" className="text-sm font-sans gradient-text italic hover:opacity-80 transition-opacity">Emagrecer na Austrália</Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const DietaEstudantesBrasileirosAustralia = () => (
  <ScheduleProvider><PageContent /></ScheduleProvider>
);

export default DietaEstudantesBrasileirosAustralia;
